import type { FeatureEnum } from '~/core/entities/CRMEnums';
import type { Property, TransactionType } from '~/core/entities/property';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { PageName } from '~/presenters/PropertyFiltersPresenter';
import type { LocationSlugFilters } from '~~/core/ports/repositories';

import type { EventBusKey } from '@vueuse/core';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { isNotEmptyNorDuplicate, slugify } from '~~/utils';

/**
 * The default filter configuration for properties.
 * @constant
 */
export const DEFAULT_FILTER: PropertyFilters = {
	transactionType: 'sell'
};

Object.freeze(DEFAULT_FILTER);

export interface FilterState {
	filters: PropertyFilters;
	_changes: number;
	_lastRequestChanges: number;
}

export type AllowedValues =
	| string
	| number
	| boolean
	| string[]
	| Exclude<TransactionType, 'rentOrSell'>
	| FeatureEnum[]
	| Property['propertyType']['identifier'][];

export const filtersEventsChangeKey = Symbol('symbol-key');
export const filtersEventsKey: EventBusKey<{ filters: PropertyFilters }> =
	Symbol('filters-events-key');

/**
 * Converts the given filters object into a ordered string.
 * Filters are sorted by key for consistent output.
 *
 * @param filters - The filters to stringify.
 * @returns - The stringified filters.
 */
function stringifyFilters(filters: PropertyFilters): string {
	return Object.entries(filters)
		.sort(([key1], [key2]) => key1.localeCompare(key2))
		.map(([key, value]) => `${key}=${value}`)
		.join('&');
}
/**
 * Checks how many filters are applied based on the presence of specified keys in the `filters` object.
 *
 * This function only validates if a filter is applied by checking if a key exists and is truthy
 * in the `filters` object. It does not verify the actual values of the fields in the object.
 *
 * @param filters - An object containing filter properties.
 * @param filtersKey - An array of keys to check within the `filters` object.
 * @returns The count of filters that are applied (truthy keys).
 *
 * @example
 * const filters = { bedrooms: true, bathrooms: false, location: true };
 * const keys = ['bedrooms', 'location'];
 * const count = isFiltersApplied(filters, keys); // Output: 2
 *
 * @example
 * const filters = { bedrooms: null, bathrooms: undefined, location: 0 };
 * const keys = ['bedrooms', 'location', 'bathrooms'];
 * const count = isFiltersApplied(filters, keys); // Output: 0
 */
export function isFiltersApplied(
	filters: PropertyFilters,
	filtersKey: (keyof PropertyFilters)[]
) {
	return filtersKey.reduce(
		(number, filterKey) => (filters[filterKey] ? number + 1 : number),
		0
	);
}

/**
 * Composable function to manage property filters and related route updates.
 *
 * @param page - The current page name, can be imoveis or mapa-de-imoveis.
 * @returns Filter management utilities.
 */
export function useFilters(page: PageName) {
	/*
	 * Event bus for filter changes.
	 * This should be trigger only when the changes count was improved
	 */
	const filterEventsChanges = useEventBus<string>(filtersEventsChangeKey);
	/*
	 * Event bus for filter changes.
	 * This should be trigger allways when the filters are updated
	 */
	const filterEvents = useEventBus<{ filters: PropertyFilters }>(
		filtersEventsKey
	);

	const route = useRoute();
	const router = useRouter();

	const _filtersState = useState<FilterState>(
		`listing-property-filters`,
		() => ({
			/**
			 * Reactive reference to the current property filters.
			 * This is the source of truth for the filters' state.
			 */
			filters: {
				...DEFAULT_FILTER
			},
			_changes: 0,
			_lastRequestChanges: 0
		})
	);

	/**
	 * Computed property that provides a reactive and readonly version of the current filters.
	 * This should be used when accessing the filters without directly mutating the state.
	 *
	 */
	const filters = computed<PropertyFilters>(() => _filtersState.value.filters);

	/**
	 * Updates a single filter key with the provided value.
	 *
	 * @param key - The filter key to update.
	 * @param value - The new value for the filter.
	 * @private
	 */
	function _updateFilter(
		key: keyof PropertyFilters,
		value: AllowedValues,
		shouldImproveChanges = true
	): void {
		_filtersState.value.filters = {
			..._filtersState.value.filters,
			[key]: value
		};

		if (shouldImproveChanges) {
			_filtersState.value._changes += 1;
		}
	}
	/**
	 * Updates a single filter key with the provided value.
	 *
	 * @param key - The filter key to update.
	 * @param value - The new value for the filter.
	 * @private
	 */
	function _removeKeyFilter(
		key: keyof PropertyFilters,
		shouldImproveChanges = true
	): void {
		delete _filtersState.value.filters[key];

		if (shouldImproveChanges) {
			_filtersState.value._changes += 1;
		}
	}

	/**
	 * Updates multiple filter keys with the provided values.
	 * @param filters - The filters to update.
	 */
	function _updateMultipleFilters(
		filters: Partial<PropertyFilters>,
		shouldImproveChanges: boolean = true
	): void {
		const keys = Object.keys(filters) as (keyof PropertyFilters)[];

		keys.forEach(key => {
			const value = filters[key];

			if (value == null || (!value && typeof value === 'number')) {
				_removeKeyFilter(key, shouldImproveChanges);
				return;
			}

			if (
				key === 'regions' &&
				Array.isArray(value) &&
				value.length < (_filtersState.value.filters.regions?.length || 0)
			) {
				_removeKeyFilter('pluscodesGroup', shouldImproveChanges);
			}

			_updateFilter(key, value, shouldImproveChanges);
		});

		if (shouldImproveChanges) {
			filterEventsChanges.emit('filters-changed-improved');
		}
		filterEvents.emit({ filters: _filtersState.value.filters });
	}

	const throtthledUpdateMultipleFilters = useThrottleFn(
		_updateMultipleFilters,
		1000
	);

	const debouncedUpdateMultipleFilters = useDebounceFn(
		_updateMultipleFilters,
		300
	);

	/**
	 * Updates the filters based on a given location slug filters.
	 * @param locationSlugFilters - The location slug filters to update the filters with.
	 */
	function updateFiltersFromLocationSlugFilters(
		locationSlugFilters?: LocationSlugFilters
	): void {
		if (!locationSlugFilters) return;

		const { city, state, region } = locationSlugFilters;

		const newRegions = [region || '', ...(filters.value?.regions || [])].filter(
			isNotEmptyNorDuplicate
		);

		const newCities = [city || '', ...(filters.value?.cities || [])].filter(
			isNotEmptyNorDuplicate
		);

		const newStates = [state || '', ...(filters.value?.states || [])].filter(
			isNotEmptyNorDuplicate
		);

		if (newRegions.length) {
			_updateFilter('regions', newRegions, false);
		}

		if (newCities.length) {
			_updateFilter('cities', newCities, false);
		}

		_updateFilter('states', newStates, false);

		filterEvents.emit({ filters: _filtersState.value.filters });
	}
	/**
	 * Generates the path for the current filters using the PropertyFiltersPresenter.
	 *
	 * @returns The generated path.
	 */
	function getPath(collection?: string): string {
		const path = PropertyFiltersPresenter.getPathFromFilters(
			filters.value,
			{},
			{ page, collection }
		);

		return path;
	}
	/**
	 * Internal function to update filters based on a given path.
	 *
	 * @param  path - The new path.
	 * @private
	 */
	function _updateFiltersFromPath(
		path: string,
		shouldImproveChanges: boolean = true
	): void {
		const newFilters = PropertyFiltersPresenter.getFiltersFromPath(
			path,
			filters.value,
			page
		);

		throtthledUpdateMultipleFilters(newFilters, shouldImproveChanges);
	}
	/**
	 * Updates filters from a new provided path.
	 *
	 * @param newPath - The new path to parse filters from.
	 */
	function updateFiltersFromNewPath(path: string): void {
		resetFilters();

		const filters = PropertyFiltersPresenter.getFiltersFromPath(
			path,
			{ ...DEFAULT_FILTER },
			page
		);
		throtthledUpdateMultipleFilters(filters, false);
	}
	/**
	 * Set filters from the current route path and dont trigger changes count change.
	 */
	function setInitialFiltersFromPath() {
		const path = route.fullPath;

		_updateFiltersFromPath(path, false);
	}

	/**
	 * Updates the route's path based on the current filters.
	 */
	function updatePathFromFilters(collection?: string): void {
		const path = getPath(collection);

		router.push(path);
	}
	/**
	 * Converts the filters to a string, optionally sanitizing specific keys.
	 *
	 * @param filters - Filters to stringify. If not provided, uses the current filters.
	 * @returns The stringified and sanitized filters.
	 */
	function getStringifiedFilters(filters?: PropertyFilters): string {
		const filtersToStringify = filters || _filtersState.value.filters;

		const sanitizedFilters = {
			...filtersToStringify,
			cities:
				filtersToStringify.cities?.filter(city => {
					const slugfiedCity = slugify(city);

					return !filtersToStringify.locationSlug?.includes(slugfiedCity);
				}) || [],
			states:
				filtersToStringify.states?.filter(state => {
					const slugfiedState = slugify(state);

					return !filtersToStringify.locationSlug?.includes(slugfiedState);
				}) || [],
			regions:
				filtersToStringify.regions?.filter(region => {
					const slugfiedRegion = slugify(region);

					return !filtersToStringify.locationSlug?.startsWith(slugfiedRegion);
				}) || []
		};

		return stringifyFilters(sanitizedFilters);
	}
	/**
	 * Clear the filters to their default values and trigger changes.
	 */
	function clearFilters() {
		const filters = {
			...DEFAULT_FILTER,
			locationSlug: 'sao-paulo-sp-brasil',
			propertyTypes: [],
			orderBy: undefined,
			sortBy: undefined,
			isExclusive: undefined,
			features: undefined,
			minArea: undefined,
			maxArea: undefined,
			minAskingPrice: undefined,
			maxAskingPrice: undefined,
			minRentPrice: undefined,
			maxRentPrice: undefined,
			minSquareMeterPrice: undefined,
			maxSquareMeterPrice: undefined,
			minCondoFee: undefined,
			maxCondoFee: undefined,
			minTax: undefined,
			maxTax: undefined,
			suites: undefined,
			bedrooms: undefined,
			bathrooms: undefined,
			parkingSpots: undefined,
			regions: undefined,
			cities: undefined,
			states: undefined,
			macroregions: undefined,
			pluscodesGroup: undefined
		};

		throtthledUpdateMultipleFilters(filters, true);
	}
	/**
	 * Resets the filters to their default values.
	 */
	function resetFilters() {
		_filtersState.value.filters = {
			...DEFAULT_FILTER
		};
	}
	/*
	 * Reset the changes counter.
	 */
	function resetChanges() {
		_filtersState.value._changes = 0;
		_filtersState.value._lastRequestChanges = 0;
	}
	/*
	 * Returns the current change count.
	 */
	function getCurrentChange() {
		return _filtersState.value._changes;
	}
	/**
	 * Updates the last request changes to the current changes count.
	 */
	function updateLastRequestChanges() {
		_filtersState.value._lastRequestChanges = _filtersState.value._changes;
	}
	/**
	 * Checks if the filters have changed since the last request.
	 *
	 * @returns Whether the filters have changed since the last request.
	 */
	function filtersHasChangedSinceLastRequest(): boolean {
		return (
			_filtersState.value._changes > _filtersState.value._lastRequestChanges
		);
	}

	return {
		filters,
		updateMultipleFilters: throtthledUpdateMultipleFilters,
		updateMultipleFiltersDebounced: debouncedUpdateMultipleFilters,
		updateFiltersFromLocationSlugFilters,
		getPath,
		updateFiltersFromNewPath,
		setInitialFiltersFromPath,
		updatePathFromFilters,
		getStringifiedFilters,
		resetFilters,
		clearFilters,
		resetChanges,
		getCurrentChange,
		updateLastRequestChanges,
		filtersHasChangedSinceLastRequest
	};
}
